/**
 * The yard details modal's verification lines: "Last RTLS update", the
 * "Verified location" sentence (worded per the PRD's scenario table), and
 * "Last manual check", kept on its own line so it never counts as RTLS coverage.
 */

export type RtlsLocationKind = 'spot' | 'range' | 'zone' | 'facility';

/** What an RTLS read verified, as flux stores it on the lineage (`rtls_location`). */
export interface RtlsLocation {
  kind?: RtlsLocationKind;
  /** `confirmed`: the YMS already had it there. `moved`: the YMS moved it there for the read. */
  outcome?: 'confirmed' | 'moved';
  range_lo?: string;
  range_hi?: string;
  unit?: 'spots' | 'docks';
  rtls_history_id?: number;
  decision_id?: number;
}

export interface VerificationLineage {
  rtls_seen_at?: string | null;
  rtls_location?: RtlsLocation | null;
  last_manual_check_at?: string | null;
}

/** Where the trailer is, by name, as the modal knows it. */
export interface VerificationPlace {
  facilityName?: string | null;
  zoneName?: string | null;
  spotName?: string | null;
}

/** What the modal is showing: a yard spot (occupied or empty) or a trailer in a zone. */
export interface VerificationSubject {
  type: 'yard_spot' | 'zone_trailer';
  /** Present when a trailer is there. */
  lineage?: VerificationLineage | null;
  occupied: boolean;
  /** An empty spot's own values. */
  lastRtlsEmptyAt?: string | null;
  lastManualCheckAt?: string | null;
}

export interface LocationVerificationLines {
  rtlsAt: string | null;
  verifiedLine: string | null;
  manualCheckAt: string | null;
  /** Zone trailers have no check button, so the manual line does not apply to them. */
  showManualCheck: boolean;
}

const KINDS: RtlsLocationKind[] = ['spot', 'range', 'zone', 'facility'];

/** The lineage's RTLS verification, or null (`rtls_location` is `{}` until one). */
export function rtlsVerification(
  lineage: VerificationLineage | null | undefined
): { seenAt: string; location: RtlsLocation } | null {
  const location = lineage?.rtls_location;
  const seenAt = lineage?.rtls_seen_at;

  if (!seenAt || !location?.kind || !KINDS.includes(location.kind)) return null;
  return { seenAt, location };
}

const inZone = (place: VerificationPlace) =>
  place.zoneName ? ` in Zone ${place.zoneName}` : '';
const atFacility = (place: VerificationPlace) =>
  place.facilityName ? ` at facility ${place.facilityName}` : '';

function rangeText(location: RtlsLocation): string {
  const unit = location.unit === 'docks' ? 'docks' : 'spots';
  const { range_lo: lo, range_hi: hi } = location;

  if (lo && hi && lo !== hi) return `, between ${unit} ${lo}–${hi}`;
  if (lo || hi) return `, at ${unit === 'docks' ? 'dock' : 'spot'} ${lo || hi}`;
  return '';
}

/** The "Verified location" sentence, or null where the PRD shows only the timestamp. */
export function verifiedLocationLine(
  lineage: VerificationLineage | null | undefined,
  place: VerificationPlace
): string | null {
  const verification = rtlsVerification(lineage);
  if (!verification) return null;

  const { kind, outcome } = verification.location;

  if (outcome === 'confirmed') {
    return kind === 'zone'
      ? `Verified location${inZone(place)}${atFacility(place)}`
      : null;
  }

  switch (kind) {
    case 'spot':
      return `Verified location${inZone(place)}${atFacility(place)}${
        place.spotName ? ` at spot ${place.spotName}` : ''
      }`;
    case 'range':
      return `Verified location${inZone(place)}${atFacility(place)}${rangeText(verification.location)}`;
    case 'zone':
      return `Verified location${inZone(place)}${atFacility(place)}`;
    case 'facility':
      return place.facilityName
        ? `Verified location at facility ${place.facilityName}, no yard spot or zone`
        : 'Verified location at the facility, no yard spot or zone';
    default:
      return null;
  }
}

/** All three lines for what the modal is showing. */
export function locationVerificationLines(
  subject: VerificationSubject,
  place: VerificationPlace
): LocationVerificationLines {
  if (subject.type === 'yard_spot' && !subject.occupied) {
    return {
      rtlsAt: subject.lastRtlsEmptyAt || null,
      verifiedLine: null,
      manualCheckAt: subject.lastManualCheckAt || null,
      showManualCheck: true
    };
  }

  return {
    rtlsAt: rtlsVerification(subject.lineage)?.seenAt ?? null,
    verifiedLine: verifiedLocationLine(subject.lineage, place),
    manualCheckAt:
      subject.type === 'yard_spot' ? subject.lineage?.last_manual_check_at || null : null,
    showManualCheck: subject.type === 'yard_spot'
  };
}
